import { Box, Flex, Text } from "@chakra-ui/react";
import type { ReactElement } from "react";
import { LuLock } from "react-icons/lu";

interface LockedSectionProps {
  title: string;
  /** What is behind this block, e.g. "Every tour on the first page in Rome". */
  prompt: string;
  /** Drives the placeholder height so the blur is proportional to what is withheld. */
  rows: number;
}

const MAX_PLACEHOLDER_ROWS = 8;
const PLACEHOLDER_WIDTHS = [92, 78, 85, 70, 88, 64, 81, 74];

/**
 * Follows the BlurredPreview precedent in components/ui/tracking-disabled-state.tsx:
 * an absolutely-positioned blurred layer behind a relative, z-indexed card.
 *
 * The blurred layer renders placeholder bars ONLY. This is not a CSS trick over
 * hidden content — an ungated response genuinely does not contain the locked
 * rows, so there is nothing here to reveal with devtools.
 *
 * Deliberately has NO button. Five of these stack on a report, and a filled CTA
 * on each one put three identical "Unlock the full report" buttons in a single
 * viewport — nagging, and three breaches of the one-filled-CTA-per-viewport rule
 * in design.md § CTA voice. The blocks are a manifest of what is withheld; the
 * single ask that follows them is where the price and the button live.
 */
export function LockedSection({
  title,
  prompt,
  rows,
}: LockedSectionProps): ReactElement {
  const placeholderCount = Math.min(Math.max(rows, 3), MAX_PLACEHOLDER_ROWS);

  return (
    <Box>
      {/* Weight and ink, not size: this names a block, and at 11px tertiary
          mono it was indistinguishable from the "showing 3 of 17" caption that
          sits a few hundred pixels above — a section label and a piece of
          metadata reading identically. Still quiet, because the block beneath
          it is withheld and should not shout. */}
      <Text
        fontFamily="var(--font-geist-mono)"
        fontSize="11px"
        fontWeight="600"
        letterSpacing="0.04em"
        color="var(--color-text-secondary)"
        mb={3}
      >
        {title}
      </Text>

      <Box position="relative" minH="260px">
        <Box
          position="absolute"
          inset={0}
          bg="var(--color-surface)"
          border="1px solid var(--color-border)"
          borderRadius="var(--radius-md)"
          overflow="hidden"
          filter="blur(4px)"
          opacity={0.6}
          pointerEvents="none"
          aria-hidden="true"
          p={4}
        >
          {Array.from({ length: placeholderCount }, (_, i) => (
            <Flex key={i} alignItems="center" gap={3} py={2.5}>
              <Box
                w="18px"
                h="10px"
                borderRadius="2px"
                bg="var(--color-border-strong)"
                flexShrink={0}
              />
              <Box
                h="10px"
                borderRadius="2px"
                bg="var(--color-border)"
                width={`${PLACEHOLDER_WIDTHS[i % PLACEHOLDER_WIDTHS.length]}%`}
              />
            </Flex>
          ))}
        </Box>

        <Flex
          position="relative"
          zIndex={1}
          minH="260px"
          alignItems="center"
          justifyContent="center"
          direction="column"
          textAlign="center"
          px={5}
        >
          <Box color="var(--color-text-tertiary)" mb={3}>
            <LuLock size={18} />
          </Box>
          <Text
            fontSize={{ base: "16px", md: "17px" }}
            fontWeight="600"
            color="var(--color-text-primary)"
            maxW="34ch"
            style={{ overflowWrap: "anywhere", minWidth: 0 }}
          >
            {prompt}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}
